import { useMemo } from 'react';
import { Button, Modal } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import {
	BsCheckCircleFill,
	BsCircle,
	BsExclamationTriangle,
	BsExclamationTriangleFill,
	BsPlusLg,
	BsXLg,
} from 'react-icons/bs';
import { useVehicle } from './api';
import { useConfiguratorState } from './store';
import { useConfiguratorFeatures } from './features';
import { collapsedTwinIds, formatCurrency } from './common';
import type { OptionId } from './domain';
import type { OrGroup } from './resolution';
import styles from './ApiValidationDialog.module.scss';

export function ApiValidationDialog() {
	const SHOW_EXCLUDED_SECTION = useConfiguratorFeatures(
		(f) => f.showExcludedSection
	);
	const pending = useConfiguratorState((s) => s.pending);
	const validating = useConfiguratorState((s) => s.validating);
	const confirmError = useConfiguratorState((s) => s.confirmError);
	const vehicleRef = useConfiguratorState((s) => s.vehicle);
	const chooseAlternative = useConfiguratorState((s) => s.chooseAlternative);
	const cancelPending = useConfiguratorState((s) => s.cancelPending);
	const confirmPending = useConfiguratorState((s) => s.confirmPending);
	const dismissConfirmError = useConfiguratorState(
		(s) => s.dismissConfirmError
	);
	const { data } = useVehicle(vehicleRef);
	const { t } = useTranslation();

	const catalog = data?.catalog ?? null;

	// Le vernici "gemello" sono collassate nel loro master: nell'anteprima delle
	// conseguenze non vanno mostrate come voci a sé (l'utente vede solo il master).
	// Restano nel payload/engine e partono comunque verso il backend.
	const collapsedTwins = useMemo(() => collapsedTwinIds(catalog), [catalog]);

	const optionName = (id: OptionId) => {
		const option = catalog?.byId.get(id);
		return (
			<>
				{option?.name ?? `Option ${id}`}
				{option?.code && (
					<span className={styles.code}> [Cod. {option.code}]</span>
				)}
			</>
		);
	};

	const priceOf = (id: OptionId) => catalog?.byId.get(id)?.basePrice ?? 0;
	const priceLabel = (id: OptionId) => formatCurrency(priceOf(id));

	const action = pending?.action ?? null;
	const cq = pending?.consequences ?? null;
	const canConfirm = pending?.canConfirm ?? false;
	const orGroups = pending?.orGroups ?? [];

	const autoAdded = (cq?.autoAdded ?? []).filter(
		(a) => !collapsedTwins.has(a.id)
	);
	const autoRemoved = (cq?.autoRemoved ?? []).filter(
		(r) => !collapsedTwins.has(r.id)
	);
	const excluded = (pending?.excluded ?? []).filter(
		(id) => !collapsedTwins.has(id)
	);

	const renderPrimary = () => {
		if (!action) return null;
		const isAdd = action.kind === 'select';
		const free = priceOf(action.id) <= 0;
		return (
			<div className={styles.section} key="your-choice">
				<div className={styles.sectionLabel}>
					{t('car_configurator_api_your_choice', {
						defaultValue: 'La tua scelta:',
					})}
				</div>
				<div className={isAdd ? styles.includedRow : styles.removedRow}>
					<span className={isAdd ? styles.includedCheck : styles.removedX}>
						{isAdd ? <BsPlusLg /> : <BsXLg />}
					</span>
					<span className={styles.radioLabel}>{optionName(action.id)}</span>
					<span
						className={`${styles.radioPrice} ${free ? styles.priceFree : ''}`}
					>
						{priceLabel(action.id)}
					</span>
				</div>
			</div>
		);
	};

	const renderAdded = () => (
		<div className={styles.section} key="will-add">
			<div className={styles.sectionLabel}>
				{autoAdded.length > 1
					? t('car_configurator_api_will_also_include_many', {
							defaultValue: 'Saranno inclusi:',
					  })
					: t('car_configurator_api_will_also_include', {
							defaultValue: 'Sarà incluso:',
					  })}
			</div>
			<div className={styles.includedList}>
				{autoAdded.map((a) => {
					const free = priceOf(a.id) <= 0;
					return (
						<div className={styles.includedRow} key={a.id}>
							<span className={styles.includedCheck}>
								<BsPlusLg />
							</span>
							<span className={styles.radioLabel}>{optionName(a.id)}</span>
							<span
								className={`${styles.radioPrice} ${
									free ? styles.priceFree : ''
								}`}
							>
								{priceLabel(a.id)}
							</span>
						</div>
					);
				})}
			</div>
		</div>
	);

	const renderRemoved = () => (
		<div className={styles.section} key="will-remove">
			<div className={styles.sectionLabel}>
				{autoRemoved.length > 1
					? t('car_configurator_api_will_also_remove_many', {
							defaultValue: 'Verranno rimossi:',
					  })
					: t('car_configurator_api_will_also_remove', {
							defaultValue: 'Verrà rimosso:',
					  })}
			</div>
			<div className={styles.removedList}>
				{autoRemoved.map((r) => (
					<div className={styles.removedRow} key={r.id}>
						<span className={styles.removedX}>
							<BsXLg />
						</span>
						<span className={styles.radioLabel}>{optionName(r.id)}</span>
						<span className={styles.radioPrice}>{priceLabel(r.id)}</span>
					</div>
				))}
			</div>
		</div>
	);

	const renderExcluded = () => (
		<div className={styles.section} key="will-exclude">
			<div className={styles.sectionLabel}>
				{excluded.length > 1
					? t('car_configurator_api_will_also_exclude_many', {
							defaultValue: 'Verranno automaticamente esclusi:',
					  })
					: t('car_configurator_api_will_also_exclude', {
							defaultValue: 'Verrà automaticamente escluso:',
					  })}
			</div>
			<div className={styles.removedList}>
				{excluded.map((id) => {
					// a struck standard feature is being dropped, not priced in
					const isStandard = catalog?.byId.get(id)?.isStandard ?? false;
					return (
						<div className={styles.removedRow} key={id}>
							<span className={styles.removedX}>
								<BsExclamationTriangle />
							</span>
							<span className={styles.radioLabel}>{optionName(id)}</span>
							{isStandard ? (
								<span className={styles.radioPrice}>
									{t('car_configurator_api_standard_feature', {
										defaultValue: 'di serie',
									})}
								</span>
							) : (
								<span className={styles.radioPrice}>{priceLabel(id)}</span>
							)}
						</div>
					);
				})}
			</div>
		</div>
	);

	const renderOrGroup = (group: OrGroup, index: number) => {
		if (!action) return null;
		const active = group.chosenId;

		return (
			<div
				className={styles.card}
				key={group.key ?? `or-${group.kind}-${index}`}
			>
				<div className={styles.reqSection}>
					<span className={styles.reqSectionLabel}>
						{optionName(group.accessoryId)}{' '}
						{group.kind === 'esclusione'
							? t('car_configurator_api_choose_one_exclude', {
									defaultValue: 'esclude una delle opzioni:',
							  })
							: t('car_configurator_api_choose_one', {
									defaultValue: 'richiede una delle opzioni compatibili:',
							  })}
					</span>
					<div className={styles.radioGroup} role="radiogroup">
						{group.branches
							// nascondi le foglie "gemello": ogni gemello ha il proprio
							// master come ramo sorello, quindi il ramo scompare senza
							// perdere scelte selezionabili
							.map((branch) => ({
								...branch,
								ids: branch.ids.filter((id) => !collapsedTwins.has(id)),
							}))
							.filter((branch) => branch.ids.length > 0)
							.map((branch) => {
								const isActive = active === branch.chosenId;
								const price = branch.ids.reduce(
									(sum, id) => sum + priceOf(id),
									0
								);
								return (
									<button
										type="button"
										key={branch.chosenId}
										role="radio"
										aria-checked={isActive}
										className={`${styles.radioRow} ${
											isActive ? styles.radioActive : ''
										}`}
										onClick={() =>
											void chooseAlternative(group, branch.chosenId)
										}
									>
										<span className={styles.radioMark}>
											{isActive ? <BsCheckCircleFill /> : <BsCircle />}
										</span>
										<span className={styles.radioLabel}>
											{branch.ids.map((id, i) => (
												<span
													key={id}
													className={i > 0 ? 'd-block' : undefined}
												>
													{optionName(id)}
												</span>
											))}
										</span>
										<span
											className={`${styles.radioPrice} ${
												price <= 0 ? styles.priceFree : ''
											}`}
										>
											{formatCurrency(price)}
										</span>
									</button>
								);
							})}
					</div>
				</div>
			</div>
		);
	};

	const renderBody = () => {
		if (!cq) return null;

		if (cq.rejected) {
			return (
				<div className={`${styles.card} ${styles.warn}`} key="rejected">
					<div className={styles.cardHead}>
						<span className={styles.warnIcon}>
							<BsExclamationTriangleFill />
						</span>
						<div className={styles.cardText}>
							<div className={styles.cardMsg}>
								{t('car_configurator_api_rejected', {
									defaultValue:
										'Impossibile aggiungere questa opzione: è in conflitto con la configurazione di base obbligatoria.',
								})}
							</div>
						</div>
					</div>
				</div>
			);
		}

		if (cq.noop && orGroups.length === 0) {
			return (
				<div className={`${styles.card} ${styles.muted}`} key="noop">
					<div className={styles.cardMsg}>
						{t('car_configurator_api_noop', {
							defaultValue: 'Nessuna modifica alla configurazione.',
						})}
					</div>
				</div>
			);
		}

		const hasSummary =
			!!action ||
			autoAdded.length > 0 ||
			autoRemoved.length > 0 ||
			(SHOW_EXCLUDED_SECTION && excluded.length > 0);

		return (
			<>
				{hasSummary && (
					<div className={styles.card}>
						{renderPrimary()}
						{autoAdded.length > 0 && renderAdded()}
						{autoRemoved.length > 0 && renderRemoved()}
						{SHOW_EXCLUDED_SECTION && excluded.length > 0 && renderExcluded()}
					</div>
				)}
				{orGroups.map(renderOrGroup)}
			</>
		);
	};

	return (
		<>
			<Modal
				show={!!pending}
				onHide={cancelPending}
				centered
				scrollable
				size="lg"
				backdrop="static"
				keyboard={false}
			>
				<Modal.Header closeButton>
					<Modal.Title>
						<div className={styles.title}>
							{t('car_configurator_api_dialog_title_update', {
								defaultValue: 'Modifica configurazione',
							})}
						</div>
					</Modal.Title>
				</Modal.Header>

				<Modal.Body
					className={`${styles.cardStack} ${validating ? styles.busy : ''}`}
				>
					{renderBody()}
				</Modal.Body>

				<Modal.Footer className="justify-content-between">
					{validating ? (
						<span className={styles.spinner}>
							<span className={styles.spinnerDot} />
							{t('car_configurator_api_validating', {
								defaultValue: 'Calcolo in corso…',
							})}
						</span>
					) : (
						<span />
					)}
					<div className="d-flex align-items-center gap-2">
						<Button
							variant="link"
							disabled={validating}
							onClick={cancelPending}
						>
							{t('car_configurator_api_cancel', { defaultValue: 'Annulla' })}
						</Button>
						<button
							type="button"
							className={styles.addBtn}
							disabled={!canConfirm || validating}
							onClick={() => confirmPending()}
						>
							{t('car_configurator_api_confirm', { defaultValue: 'Conferma' })}
						</button>
					</div>
				</Modal.Footer>
			</Modal>

			<Modal show={!!confirmError} onHide={dismissConfirmError} centered>
				<Modal.Header closeButton>
					<Modal.Title>
						{t('car_configurator_api_backend_reject_title', {
							defaultValue: 'Configurazione non valida',
						})}
					</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					<p>
						{t('car_configurator_api_backend_reject_msg', {
							defaultValue: 'Il sistema ha rifiutato questa configurazione:',
						})}
					</p>
					<ul>
						{(confirmError ?? []).map((m, i) => (
							<li key={i}>{m}</li>
						))}
					</ul>
				</Modal.Body>
				<Modal.Footer>
					<Button variant="link" onClick={dismissConfirmError}>
						{t('car_configurator_api_close', { defaultValue: 'Chiudi' })}
					</Button>
				</Modal.Footer>
			</Modal>
		</>
	);
}
